import type { StaffTaskResponse, StaffTaskRow, StaffTaskSource } from '@myfm/contracts';
import { staffStatus } from '@myfm/domain';

export const taskSourceLabels: Record<StaffTaskSource, string> = { work: 'Repairs and work', actions: 'Actions', mail: 'Mail', reports: 'Checklists and inspections', onboarding: 'Building onboarding', questions: 'Engine questions' };
export const taskDueLabels: Record<string, string> = { overdue: 'Overdue', today: 'Due today', upcoming: 'Upcoming', undated: 'No date recorded' };
export type RetainedTask = { item: StaffTaskRow; lastKnownAt: string };
export type TaskPageSnapshot = { data: StaffTaskResponse; stale: RetainedTask[] };

export function taskStateLabel(item: Pick<StaffTaskRow, 'source' | 'state'>): string {
  if (item.source === 'work') return staffStatus(item.state).label;
  const labels: Record<string, string> = { Submitted: 'Awaiting review', Returned: 'Returned for correction', Open: 'Open', 'In progress': 'In progress', 'Awaiting evidence': 'Awaiting evidence',
    'Awaiting verification': 'Awaiting verification', New: 'New', Triaged: 'Initial review complete', Assigned: 'Assigned',
    'Draft ready': 'Draft ready', 'Awaiting response': 'Awaiting response', Failed: 'Handling failed' };
  return labels[item.state] ?? 'Status unavailable';
}

/** Audit F-044 (change #85): one status for the same record on Home, the building Overview and My Tasks.
 * An overdue or due-today date is the most useful status; otherwise the record's own status is shown. */
export function taskStatusBadge(item: Pick<StaffTaskRow, 'source' | 'state' | 'dueState'>): { text: string; tone: 'danger' | 'warning' | 'neutral' } {
  if (item.dueState === 'overdue') return { text: 'Overdue', tone: 'danger' };
  if (item.dueState === 'today') return { text: 'Due today', tone: 'warning' };
  return { text: taskStateLabel(item), tone: 'neutral' };
}

/** Retain only sources that failed this refresh, within the exact same query/scope. */
export function mergeTaskPage(previous: TaskPageSnapshot | undefined, next: StaffTaskResponse): TaskPageSnapshot {
  const unavailable = new Set(next.coverage.filter(source => source.status === 'unavailable').map(source => source.source));
  const current = new Set(next.items.map(item => item.key));
  const old = [...(previous?.stale ?? []), ...(previous?.data.items ?? []).map(item => ({ item,
    lastKnownAt: previous!.data.coverage.find(source => source.source === item.source)?.loadedAt ?? previous!.data.calculatedAt }))];
  return { data: next, stale: [...new Map(old.filter(row => unavailable.has(row.item.source) && !current.has(row.item.key))
    .map(row => [row.item.key, row])).values()] };
}

export function allRetainedTasks(snapshot: TaskPageSnapshot): RetainedTask[] {
  return [...new Map([...snapshot.stale, ...snapshot.data.items.map(item => ({ item,
    lastKnownAt: snapshot.data.coverage.find(source => source.source === item.source)?.loadedAt ?? snapshot.data.calculatedAt }))]
    .map(row => [row.item.key, row])).values()];
}
